import DeleteIcon from "@mui/icons-material/Delete";
import SyncAltIcon from "@mui/icons-material/SyncAlt";
import {
  Box,
  Button,
  Container,
  Dialog,
  IconButton,
  Tooltip,
  Typography,
} from "@mui/material";
import DeleteEntityDialog from "components/DeleteEntityDialog";
import LazyTable, {
  DEFAULT_ROWS_PER_PAGE,
  TableCellType,
  TableSortType,
} from "components/LazyTable";
import SnackbarMessage from "components/SnackbarMessage";
import { withErrorBoundary } from "hocs/withErrorBoundary";
import useGlobalContext from "hooks/globalData";
import useBoolean from "hooks/useBoolean";
import { useStrictQuery } from "hooks/useStrictQuery";
import useTrashablePromises from "hooks/useTrashablePromises";
import { useCallback, useMemo, useState } from "react";
import { DropResult } from "react-beautiful-dnd";
import { FormattedMessage as T, useIntl } from "react-intl";
import { useQuery, useQueryClient } from "react-query";
import { Link } from "react-router-dom";
import {
  ListRequestOfBaseFilter,
  ListRequestOfSkuRelationshipFilter,
  ListResponseOfParentSku,
  ListResponseOfSkuRelationship,
  ParentSku,
  SkuRelationship,
  SkuRelationshipFilter,
  SkuRelationshipSequenceRequest,
} from "services/KnowfullyAdminClient";
import {
  Severity,
  SkuRelationshipTypeIds,
  SnackbarMessageProps,
} from "types/common-types";
import { SkuFormProps } from "types/sku-related-types";
import { SkuContents } from "../SkuContents";
import SkuRelationshipForm from "./SkuRelationshipForm";

const styles = {
  link: {
    textDecoration: "none",
    color: "inherit",
  },
};

const SkuRelatedSkus = ({
  sku,
  onSkuUpdated,
  onClean,
  ...rest
}: SkuFormProps) => {
  const { registerPromise } = useTrashablePromises();
  const { formatMessage: t } = useIntl();
  const queryClient = useQueryClient();
  const { skuService, skuDetailsUrl } = useGlobalContext();
  const [deleting, setDeleting] = useState<SkuRelationship | null>(null);
  const [
    snackbarMessage,
    setSnackbarMessage,
  ] = useState<SnackbarMessageProps | null>(null);
  const [createOpen, createOpenActions] = useBoolean(false);
  const [autoRegisterPage, setAutoRegisterPage] = useState(0);
  const [autoRegisterRowsPerPage, setAutoRegisterRowsPerPage] = useState(
    DEFAULT_ROWS_PER_PAGE,
  );
  const [suggestedCoursesPage, setSuggestedCoursesPage] = useState(0);
  const [
    suggestedCoursesRowsPerPage,
    setSuggestedCoursesRowsPerPage,
  ] = useState(DEFAULT_ROWS_PER_PAGE);
  const [includedInPage, setIncludedInPage] = useState(0);
  const [includedInRowsPerPage, setIncludedInRowsPerPage] = useState(
    DEFAULT_ROWS_PER_PAGE,
  );
  const [includedInOrder, setIncludedInOrder] = useState<TableSortType>("asc");
  const [includedInOrderBy, setIncludedInOrderBy] = useState("parentSku.Sku");

  const handleCancel = useCallback(() => {
    createOpenActions.setFalse();
    onClean?.();
  }, [createOpenActions, onClean]);

  const handleDelete = useCallback(async () => {
    const id = deleting?.id;
    const typeId = deleting?.skuRelationshipTypeId;
    setDeleting(null);
    if (id) {
      try {
        await registerPromise(skuService.deleteChildSkusRelated(id));
        queryClient.invalidateQueries([
          "skuService.getChildSkusRelated",
          sku.id!,
          typeId,
        ]);
        setSnackbarMessage(
          new SnackbarMessageProps(
            t({ id: "skuRelationShipDeleted" }),
            Severity.Warning,
          ),
        );
      } catch (err: any) {
        console.log(err);
      }
    }
  }, [deleting, queryClient, registerPromise, sku.id, skuService, t]);

  const {
    data: autoRegisterData,
    isFetching: isFetchingAutoRegister,
  } = useStrictQuery<ListResponseOfSkuRelationship>(
    [
      "skuService.getChildSkusRelated",
      sku.id!,
      SkuRelationshipTypeIds.AutoRegister,
      autoRegisterPage,
      autoRegisterRowsPerPage,
    ],
    () =>
      skuService.getChildSkusRelated(
        new ListRequestOfSkuRelationshipFilter({
          pageNumber: autoRegisterPage,
          pageSize: autoRegisterRowsPerPage,
          parameters: new SkuRelationshipFilter({
            skuId: sku.id!,
            skuRelationshipTypeId: SkuRelationshipTypeIds.AutoRegister,
          }),
        }),
      ),
  );

  const {
    data: suggestedCoursesData,
    isFetching: isFetchingSuggestedCourses,
  } = useStrictQuery<ListResponseOfSkuRelationship>(
    [
      "skuService.getChildSkusRelated",
      sku.id!,
      SkuRelationshipTypeIds.SuggestedCourses,
      suggestedCoursesPage,
      suggestedCoursesRowsPerPage,
    ],
    () =>
      skuService.getChildSkusRelated(
        new ListRequestOfSkuRelationshipFilter({
          pageNumber: suggestedCoursesPage,
          pageSize: suggestedCoursesRowsPerPage,
          parameters: new SkuRelationshipFilter({
            skuId: sku.id!,
            skuRelationshipTypeId: SkuRelationshipTypeIds.SuggestedCourses,
          }),
        }),
      ),
  );

  const {
    data: includedInData,
    isFetching: isFetchingIncludedIn,
  } = useQuery<ListResponseOfParentSku>(
    [
      "skuService.getParentSkusOfChildSku",
      sku.id!,
      includedInPage,
      includedInRowsPerPage,
      includedInOrder,
      includedInOrderBy,
    ],
    () =>
      skuService.getParentSkusOfChildSku(
        sku.id!,
        ListRequestOfBaseFilter.fromJS({
          pageNumber: includedInPage,
          pageSize: includedInRowsPerPage,
          sortField: includedInOrderBy,
          sortDescending: includedInOrder === "desc",
        }),
      ),
  );

  const autoRegisterSkus = useMemo(() => autoRegisterData?.data ?? [], [
    autoRegisterData?.data,
  ]);
  const suggestedCoursesSkus = useMemo(() => suggestedCoursesData?.data ?? [], [
    suggestedCoursesData?.data,
  ]);
  const includedInSkus = useMemo(() => includedInData?.data ?? [], [
    includedInData?.data,
  ]);

  const onSkuRelationshipCreated = () => {
    createOpenActions.setFalse();
    setSnackbarMessage(
      new SnackbarMessageProps(t({ id: "skuRelationshipCreated" })),
    );
  };

  const createDragHandler = (typeId: string) => async (result: DropResult) => {
    try {
      if (!result.destination) return;
      await skuService.saveSkuRelationshipSequence(
        sku.id!,
        new SkuRelationshipSequenceRequest({
          skuRelationshipTypeId: typeId,
          skuRelationshipId: result.draggableId,
          sequence: result.destination?.index,
        }),
      );
      queryClient.invalidateQueries([
        "skuService.getChildSkusRelated",
        sku.id!,
        typeId,
      ]);
    } catch (error) {
      console.log(error);
    }
  };

  const SkuTitle = ({ value }: { value: SkuRelationship }) => (
    <Box display="flex" flexDirection="row" alignItems="center" gap={0.5}>
      {value.isBidirectional && (
        <Tooltip title={t({ id: "bidirectional" })}>
          <SyncAltIcon fontSize="small" color="primary" />
        </Tooltip>
      )}
      <Link style={styles.link} to={skuDetailsUrl(value.childSkuId)}>
        {value.title}
      </Link>
    </Box>
  );

  const SkuName = ({ value }: { value: SkuRelationship }) => (
    <Link style={styles.link} to={skuDetailsUrl(value.childSkuId)}>
      {value.name}
    </Link>
  );

  const DeleteButton = ({ value }: { value: SkuRelationship }) => (
    <Box textAlign="center">
      <IconButton size="small" onClick={() => setDeleting(value)}>
        <DeleteIcon />
      </IconButton>
    </Box>
  );

  const ParentSkuName = ({ value }: { value: ParentSku }) => (
    <Link style={styles.link} to={skuDetailsUrl(value.skuId)}>
      {value.sku}
    </Link>
  );

  const ParentSkuTitle = ({ value }: { value: ParentSku }) => (
    <Link style={styles.link} to={skuDetailsUrl(value.skuId)}>
      {value.title}
    </Link>
  );

  const skuRelationshipCells: Array<TableCellType> = [
    {
      accessor: "value",
      width: "80%",
      type: "node",
      View: SkuTitle,
      label: t({ id: "skuRelationShipSkuTitleLabel" }),
      noSort: true,
    },
    {
      accessor: "value",
      width: "17%",
      type: "node",
      View: SkuName,
      label: t({ id: "skuRelationShipSkuNameLabel" }),
      noSort: true,
    },
    {
      accessor: "value",
      align: "center",
      width: "3%",
      type: "node",
      View: DeleteButton,
      label: "",
      noSort: true,
    },
  ];

  const includedInCells: Array<TableCellType> = [
    {
      accessor: "sku",
      customSortAccessor: "parentSku.Sku",
      type: "node",
      View: ParentSkuName,
      label: t({ id: "skuRelationShipSkuNameLabel" }),
    },
    {
      accessor: "title",
      customSortAccessor: "parentSku.Title",
      type: "node",
      View: ParentSkuTitle,
      label: t({ id: "skuRelationShipSkuTitleLabel" }),
    },
  ];

  return (
    <Container>
      <Box display="flex" justifyContent="flex-end" width="100%" mb={2}>
        <Button
          variant="outlined"
          color="primary"
          onClick={createOpenActions.setTrue}
        >
          <T id="addSkuRelationshipButton" />
        </Button>
      </Box>

      {autoRegisterSkus.length > 0 && (
        <Box mb={3}>
          <Typography variant="h6" gutterBottom>
            <T id="autoRegisterTitle" />
          </Typography>
          <LazyTable
            {...{
              rowCount: autoRegisterData?.totalRecords ?? 0,
              rows: autoRegisterSkus,
              loading: isFetchingAutoRegister || deleting !== null,
              dense: true,
              cells: skuRelationshipCells,
              page: autoRegisterPage,
              setPage: setAutoRegisterPage,
              order: "asc",
              orderBy: "",
              rowsPerPage: autoRegisterRowsPerPage,
              setRowsPerPage: setAutoRegisterRowsPerPage,
              setOrder: () => {},
              setOrderBy: () => {},
              allowPagination: true,
              allowDragAndDrop: true,
              onDragAndDropEnd: createDragHandler(
                SkuRelationshipTypeIds.AutoRegister,
              ),
            }}
          />
        </Box>
      )}

      {suggestedCoursesSkus.length > 0 && (
        <Box mb={3}>
          <Typography variant="h6" gutterBottom>
            <T id="suggestedCoursesTitle" />
          </Typography>
          <LazyTable
            {...{
              rowCount: suggestedCoursesData?.totalRecords ?? 0,
              rows: suggestedCoursesSkus,
              loading: isFetchingSuggestedCourses || deleting !== null,
              dense: true,
              cells: skuRelationshipCells,
              page: suggestedCoursesPage,
              setPage: setSuggestedCoursesPage,
              order: "asc",
              orderBy: "",
              rowsPerPage: suggestedCoursesRowsPerPage,
              setRowsPerPage: setSuggestedCoursesRowsPerPage,
              setOrder: () => {},
              setOrderBy: () => {},
              allowPagination: true,
              allowDragAndDrop: true,
              onDragAndDropEnd: createDragHandler(
                SkuRelationshipTypeIds.SuggestedCourses,
              ),
            }}
          />
        </Box>
      )}

      {includedInSkus.length > 0 && (
        <Box mb={3}>
          <Typography variant="h6" gutterBottom>
            <T id="includedInTitle" />
          </Typography>
          <LazyTable
            {...{
              rowCount: includedInData?.totalRecords ?? 0,
              rows: includedInSkus as any[],
              loading: isFetchingIncludedIn,
              dense: true,
              cells: includedInCells,
              page: includedInPage,
              setPage: setIncludedInPage,
              order: includedInOrder,
              setOrder: setIncludedInOrder,
              orderBy: includedInOrderBy,
              setOrderBy: setIncludedInOrderBy,
              rowsPerPage: includedInRowsPerPage,
              setRowsPerPage: setIncludedInRowsPerPage,
              allowPagination: true,
            }}
          />
        </Box>
      )}

      {sku?.hasChild && (
        <Box mb={3}>
          <Typography variant="h6" gutterBottom>
            <T id="containsTitle" />
          </Typography>
          <SkuContents sku={sku} readonly={true} />
        </Box>
      )}

      <DeleteEntityDialog
        dialogTitleKey="skuRelationShipSkuDeleteTitle"
        contentKey="skuRelationShipSkuDeleteQuestion"
        open={!!deleting}
        handleDelete={handleDelete}
        onCancel={() => setDeleting(null)}
      />

      <Dialog open={createOpen} disableEnforceFocus maxWidth="sm" fullWidth>
        <SkuRelationshipForm
          sku={sku}
          onSkuUpdated={onSkuUpdated}
          onSkuRelationshipCreated={onSkuRelationshipCreated}
          onCancel={handleCancel}
          onClean={onClean}
          {...rest}
        />
      </Dialog>

      <SnackbarMessage
        snackbarMessage={snackbarMessage}
        setSnackbarMessage={setSnackbarMessage}
      />
    </Container>
  );
};

export default withErrorBoundary(SkuRelatedSkus);
